/**
 * Pure validation for the GloriaFood restaurant-link field. The import takes
 * the LINK to the restaurant's GloriaFood storefront — the foodbooking.com
 * ordering page, a white-label domain, or the restaurant's own website with
 * the embedded ordering widget. The scraper resolves the restaurant from the
 * page itself, so the host cannot be whitelisted client-side.
 *
 * What CAN be named are the shapes that are never a storefront: the vendor's
 * own gloriafood.com and the `/admin` menu-editor console every white-label
 * reseller mounts (two production outlets pasted exactly that), and another
 * platform's page — Clover, Lieferando, Google, Instagram — which can never
 * host the widget (PROD-521). A bare restaurant UID is turned into its
 * foodbooking.com link rather than rejected. Mirrors the backend's
 * App\Rules\GloriaFoodListingUrl and NormaliseGloriaFoodListingUrl — keep them
 * in step. See tests/components/menu/restaurantLinkValidation.test.js.
 */

/**
 * True when the trimmed value parses as an http(s) URL.
 * Empty / non-string values are treated as invalid (the field is required) —
 * callers decide whether to surface an error only once the user has typed.
 */
export const isValidRestaurantLink = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return false;
  }

  try {
    const url = new URL(value.trim());

    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const VENDOR_HOST = 'gloriafood.com';

/**
 * True when the value is a GloriaFood back-office link rather than the page
 * customers order from. Non-URLs return false — `isValidRestaurantLink` owns
 * those, so the field never shows two messages for one mistake.
 */
export const isGloriaFoodBackOfficeLink = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return false;
  }

  let url;

  try {
    url = new URL(value.trim());
  } catch {
    return false;
  }

  const host = url.hostname.toLowerCase();

  return (
    host === VENDOR_HOST ||
    host.endsWith(`.${VENDOR_HOST}`) ||
    url.pathname === '/admin' ||
    url.pathname.startsWith('/admin/')
  );
};

/**
 * Hosts of other ordering, marketplace, search and social platforms, matched
 * as the host or any subdomain of it (`indiahousebp.cloveronline.com`).
 * `google` is the `.google` TLD (share.google). A blocklist, never a
 * whitelist: an unknown host may be the restaurant's own site.
 */
const WRONG_PLATFORM_HOSTS = [
  // Other ordering and POS systems
  'bolt.eu',
  'chownow.com',
  'clover.com',
  'cloveronline.com',
  'deliverect.com',
  'menufy.com',
  'slicelife.com',
  'squareup.com',
  'toasttab.com',
  // Marketplaces
  'bistro.sk',
  'doordash.com',
  'glovoapp.com',
  'grubhub.com',
  'justeat.it',
  'menulog.com.au',
  'pyszne.pl',
  'seamless.com',
  'skipthedishes.com',
  'takeaway.com',
  'talabat.com',
  'thuisbezorgd.nl',
  'ubereats.com',
  'wolt.com',
  // Search, maps and social
  'facebook.com',
  'fb.com',
  'fb.me',
  'g.page',
  'goo.gl',
  'google',
  'instagram.com',
  'linktr.ee',
  'tiktok.com',
];

/**
 * Brands with one site per country, matched on any TLD including two-level
 * ones (google.de, deliveroo.co.uk, google.com.au).
 */
const WRONG_PLATFORM_BRANDS = [
  'deliveroo',
  'foodora',
  'foodpanda',
  'google',
  'just-eat',
  'lieferando',
  'opentable',
  'tripadvisor',
  'yelp',
];

/**
 * Website builders under a blocked brand. The site on them is the restaurant's
 * own, which may embed the widget, so they are judged like any unknown host.
 */
const OWN_WEBSITE_HOSTS = ['sites.google.com'];

// Brand names are plain [a-z-] words, so they need no escaping.
const WRONG_PLATFORM_BRAND_PATTERN = new RegExp(`(^|\\.)(${WRONG_PLATFORM_BRANDS.join('|')})\\.(com?\\.)?[a-z]{2,3}$`);

const hostIsOrIsUnder = (host, domain) => host === domain || host.endsWith(`.${domain}`);

const hostIsOrIsUnderAny = (host, domains) => domains.some((domain) => hostIsOrIsUnder(host, domain));

/**
 * True when the value is a link to another platform that can never host the
 * GloriaFood ordering widget. Non-URLs return false, as above.
 */
export const isWrongPlatformLink = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return false;
  }

  let url;

  try {
    url = new URL(value.trim());
  } catch {
    return false;
  }

  const host = url.hostname.toLowerCase();

  if (hostIsOrIsUnderAny(host, OWN_WEBSITE_HOSTS)) {
    return false;
  }

  return hostIsOrIsUnderAny(host, WRONG_PLATFORM_HOSTS) || WRONG_PLATFORM_BRAND_PATTERN.test(host);
};

const RESTAURANT_UID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const FOODBOOKING_MENU_URL = 'https://www.foodbooking.com/ordering/restaurant/menu?restaurant_uid=';

/**
 * A bare GloriaFood restaurant UID becomes its foodbooking.com menu link —
 * every UID resolves there, so partners who paste only the UID from their
 * GloriaFood account need not be sent back. Lowercased like the scraper does,
 * so one restaurant keeps one import identity. Anything else is returned as-is.
 */
export const normalizeGloriaFoodLink = (value) => {
  if (typeof value !== 'string' || !RESTAURANT_UID.test(value.trim())) {
    return value;
  }

  return `${FOODBOOKING_MENU_URL}${value.trim().toLowerCase()}`;
};

export const GloriaFoodLinkError = {
  InvalidLink: 'invalid_link',
  BackOffice: 'back_office',
  WrongPlatform: 'wrong_platform',
};

/**
 * The one mistake a GloriaFood link value makes, checked on its normalized
 * form so a bare UID counts as the link it becomes. Null when it may import.
 */
export const getGloriaFoodLinkError = (value) => {
  const link = normalizeGloriaFoodLink(value);

  if (!isValidRestaurantLink(link)) {
    return GloriaFoodLinkError.InvalidLink;
  }

  if (isGloriaFoodBackOfficeLink(link)) {
    return GloriaFoodLinkError.BackOffice;
  }

  if (isWrongPlatformLink(link)) {
    return GloriaFoodLinkError.WrongPlatform;
  }

  return null;
};

const DOORDASH_HOST = 'doordash.com';

export const DoorDashLinkError = {
  NotStorePage: 'not_store_page',
  StoreNumberOnly: 'store_number_only',
};

/**
 * The one mistake a DoorDash link value makes, or null when it may import.
 *
 * A DoorDash import reads a store page: the host must be doordash.com (or a
 * subdomain) and the path must carry a `store` segment followed by the store's
 * slug-id. That covers every shape seen live in the US, CA and AU:
 * `/store/{slug}-{id}/`, `/store/{slug}/{id}/`, a locale prefix
 * (`/en-AU/store/…`) or suffix (`/store/…/en-US`), with or without a trailing
 * slash or query string.
 *
 * A link with only the store number (`/store/351102/`) is its own mistake:
 * Actowiz cannot read it even for a store that imports fine with its name in
 * the link. Mirrors the backend's App\Rules\DoorDashStoreUrl — keep them in step.
 */
export const getDoorDashLinkError = (value) => {
  if (!isValidRestaurantLink(value)) {
    return DoorDashLinkError.NotStorePage;
  }

  const url = new URL(value.trim());
  const host = url.hostname.toLowerCase();

  if (host !== DOORDASH_HOST && !host.endsWith(`.${DOORDASH_HOST}`)) {
    return DoorDashLinkError.NotStorePage;
  }

  const segments = url.pathname
    .toLowerCase()
    .split('/')
    .filter((segment) => segment !== '');
  const store = segments.indexOf('store');
  const storeSegment = store === -1 ? undefined : segments[store + 1];

  if (!storeSegment) {
    return DoorDashLinkError.NotStorePage;
  }

  return /^\d+$/.test(storeSegment) ? DoorDashLinkError.StoreNumberOnly : null;
};

/** True when the value is a DoorDash store link that can import. */
export const isDoorDashStoreLink = (value) => getDoorDashLinkError(value) === null;

// The Deliveroo brand on any TLD, including two-level ones (deliveroo.co.uk).
const DELIVEROO_HOST_PATTERN = /(^|\.)deliveroo\.(com?\.)?[a-z]{2,3}$/;

export const DeliverooLinkError = {
  NotMenuPage: 'not_menu_page',
};

/**
 * The one mistake a Deliveroo link value makes, or null when it may import.
 *
 * A Deliveroo import reads a restaurant menu page: the host must be a
 * Deliveroo domain (deliveroo.co.uk, deliveroo.ie, …) and the path must carry
 * a `menu` segment followed by city, area and restaurant slug —
 * `/menu/{city}/{area}/{slug}`, optionally behind a locale segment
 * (`/fr/menu/…`), with or without a trailing slash or query string. Every
 * production link has that shape. A city listing, the home page or another
 * platform's link cannot import.
 *
 * Mirrors the backend's App\Rules\DeliverooMenuUrl — keep them in step.
 */
export const getDeliverooLinkError = (value) => {
  if (!isValidRestaurantLink(value)) {
    return DeliverooLinkError.NotMenuPage;
  }

  const url = new URL(value.trim());

  if (!DELIVEROO_HOST_PATTERN.test(url.hostname.toLowerCase())) {
    return DeliverooLinkError.NotMenuPage;
  }

  const segments = url.pathname
    .toLowerCase()
    .split('/')
    .filter((segment) => segment !== '');
  const menu = segments.indexOf('menu');

  return menu !== -1 && segments.length - menu - 1 >= 3 ? null : DeliverooLinkError.NotMenuPage;
};

/** True when the value is a Deliveroo menu link that can import. */
export const isDeliverooMenuLink = (value) => getDeliverooLinkError(value) === null;
